import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Box } from '@mui/material'

import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { MotivationDiagnosis as MotivationDiagnosisData } from '../../types'
import { DiagnosisColumn } from './DiagnosisColumn'
import { MotivationDiagnosisLegend } from './MotivationDiagnosisLegend'
import { PortfolioDiagnosisCard, UnavailableDiagnosisCard } from './PortfolioCard'

interface MotivationDiagnosisProps {
  diagnosis: MotivationDiagnosisData
  /**
   * Whether the game has a Game & Live Events motivation profile. Without one
   * there is nothing to compare the creatives against.
   */
  available: boolean
  /** Game name used to make the portfolio-summary text concrete. */
  gameName: string
  /** Game app icon, shown at each gap bar's goalpost marker in place of the dot. */
  gameIcon?: string
  /**
   * Hides the top portfolio-summary "DIAGNOSIS" card, leaving just the gap
   * legend and blind-spot cards. Used by the Overview preview, which already
   * carries its own narrative summary above this section.
   */
  hideSummary?: boolean
  /**
   * Hides the inline gap legend. Used when the legend is hoisted elsewhere (e.g.
   * into the enclosing section card header) so it isn't rendered twice.
   */
  hideLegend?: boolean
}

/**
 * "What's working, what's not?" section that surfaces each game's motivation
 * blind spots — motivations the game (design or events) emphasises but
 * creatives barely cover — so the gaps to close are front and centre.
 *
 * Cards open the same per-motivation drilldown the radar/bar charts use, so
 * the section doubles as a navigable index of the motivations that matter
 * most for this game.
 *
 * The column and cards live in sibling files
 * ({@link DiagnosisColumn}, {@link DiagnosisCard}, {@link PortfolioDiagnosisCard},
 * {@link UnavailableDiagnosisCard}); this file is intentionally a thin
 * orchestrator that picks the right top-level layout based on `available`.
 */
export const MotivationDiagnosis: FC<MotivationDiagnosisProps> = ({ diagnosis, available, gameName, gameIcon, hideSummary, hideLegend }) => {
  const { t } = useTranslation('ua-insights')
  const { motivationLabel } = useUATaxonomy()
  const { aligned, blindSpots } = diagnosis

  // Without an in-game (Game & Live Events) signal we can't compare against
  // the creative ads, so the blind-spot bucket carries no meaning. Surface a
  // disclaimer instead of an empty column so the user understands the
  // diagnosis is unavailable for this game (rather than "everything is fine").
  if (!available) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <UnavailableDiagnosisCard gameName={gameName} />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pb: 0.5 }}>
      {!hideSummary && (
        <PortfolioDiagnosisCard
          text={
            blindSpots.length > 0
              ? t('diagnosis_summary_blind_spots', { game: gameName, count: blindSpots.length, worst: motivationLabel(blindSpots[0].motivation) })
              : t(aligned.length > 0 ? 'diagnosis_summary_no_blind_spots' : 'diagnosis_summary_spread', { game: gameName })
          }
          subtitle={t('motivation_diagnosis_subtitle', { game: gameName })}
        />
      )}

      {/* Single section-level legend mirroring the gap bar's vocabulary
          (delivered fill, target goalpost, uncovered gap) so each card stays
          label-free and scannable. Hidden when hoisted into the section header. */}
      {!hideLegend && <MotivationDiagnosisLegend gameIcon={gameIcon} />}

      <DiagnosisColumn category="blind_spot" cards={blindSpots} emptyText={t('diagnosis_blind_spots_empty')} gameIcon={gameIcon} />
    </Box>
  )
}
